/**
 * addFieldStore — AK-334/335 «Tap-to-field» v1.
 *
 * ОТДЕЛЬНЫЙ лёгкий zustand-стор (как measureStore/editorStore, ADR-038), НЕ часть
 * mapReducer/useMapStoreV2. Держит UI-машину состояний режима «+ Поле» + черновик
 * геометрии + запомненное на сессию хозяйство. Геометрия рисуется императивным
 * AddFieldController по подписке (v2.tsx); стор знает только про фазы и данные.
 *
 * Взаимоисключение инструментов (линейка/редактор/«+ Поле») — как у measure/editor:
 * включение гасит соседей (RightRail onClick). Здесь `active` = режим включён.
 *
 * AK-373 «Пересборка режима добавления»: способ добавления выбирается ЯВНО
 * (сегмент-переключатель «Автопоиск/Нарисовать/Файл»), а не скрытым жестом.
 * `method` — активный способ; фазовая машина одна на все способы, но «Очистить»/
 * «Отмена» ВСЕГДА возвращают в armed ТЕКУЩЕГО способа (не переключают способ —
 * это была грабля п.5: «Очистить» у квадрата непредсказуемо включал автопоиск).
 *
 * Способы (`method`):
 *   detect  — «Автопоиск»: тап по полю → детект границ (SAM)
 *   draw    — «Нарисовать»: поточечное рисование контура (terra-draw polygon)
 *   import  — «Файл»: загрузка geojson/kml/shp с превью на карте
 *
 * Машина фаз (`phase`):
 *   idle       — режим выключен (active=false)
 *   armed      — способ выбран, ждём стартового действия способа:
 *                  detect → тап по полю; draw → рисование; import → дропзона
 *   detecting  — (detect) тап был, крутится детект (пульс + чип «Ищу границы…»)
 *   drawing    — (draw) идёт поточечное рисование контура в редакторе
 *   draft      — пришёл контур, показываем amber-пунктир + панель «Сохранить/…»
 *   editing    — черновик передан в редактор контура (AK-321/322)
 *   form       — форма сохранения (имя + хозяйство + культура)
 *
 * Переход editing живёт в editorStore (его active), но фазу помним здесь, чтобы
 * вернуться из редактора в форму, а не в draft.
 */
import { create } from 'zustand';
import type { DelineateGeometry } from './delineateApi';

export type AddFieldPhase =
  | 'idle'
  | 'armed'
  | 'detecting'
  | 'drawing'
  | 'draft'
  | 'editing'
  | 'form';

/** Способ добавления поля (AK-373 сегмент-переключатель). */
export type AddFieldMethod = 'detect' | 'draw' | 'import';

/** [lng, lat]. */
export type Point = [number, number];

interface AddFieldStore {
  /** Режим «+ Поле» включён. */
  active: boolean;
  /** Активный способ добавления (сегмент-переключатель). */
  method: AddFieldMethod;
  /** Текущая фаза машины. */
  phase: AddFieldPhase;
  /** Точка последнего тапа (для пульса-лоадера), либо null. */
  tapPoint: Point | null;
  /** Черновик-геометрия (amber-пунктир / что уйдёт в редактор / сохранение). */
  draftGeometry: DelineateGeometry | null;
  /** Площадь черновика, га (из детекта или пересчёт после редактора). */
  draftAreaHa: number | null;
  /** Тост об ошибке детекции (не распознано / сервис недоступен), либо null. */
  errorToast: string | null;
  /** Тост успеха («✓ добавлено в …»), либо null. */
  successToast: string | null;

  /**
   * Запомненное на сессию хозяйство (онбординг полей подряд без перевыбора).
   * Сбрасывается при перезагрузке вкладки (в сторе нет persist).
   */
  sessionFarmId: string | null;
  sessionFarmName: string | null;

  /**
   * Сколько полей сохранено за эту сессию режима. Нужен внешним экранам,
   * чтобы поймать момент «первое поле появилось» (AK-584: карточка «Готово»
   * с подводкой про снимки после входа из мастера). Тост для этого не годится —
   * он сам гаснет через 4 секунды.
   */
  savedCount: number;

  /**
   * Монотонный сигнал «передать черновик в редактор». Инкрементится при
   * «Редактировать»; v2.tsx по изменению зовёт editor.loadDraft(geometry).
   */
  editSignal: number;

  /**
   * Монотонный сигнал «нарисовать контур поточечно с нуля» (AK-373).
   * Инкрементится при старте способа «Нарисовать» (armed(draw) → drawing);
   * v2.tsx по изменению включает редактор и запускает terra-draw polygon-draw-
   * mode (клик = точка, замыкание = контур). Заменяет прежний seed-квадрат
   * (#411): пользователь рисует форму поля сам. Отдельный сигнал от editSignal:
   * тот стартует с draft-геометрии детекта, этот — с чистого рисования.
   */
  manualDrawSignal: number;

  /**
   * Монотонный сигнал «сразу открыть загрузку файла». Инкрементится при входе
   * на карту с `?addField=1&import=1` (кнопка «Импорт из файла» в списке полей —
   * AK-369 «одна дверь»). AddFieldPanel по изменению раскрывает дропзону вместо
   * тапа. Отдельный сигнал, чтобы не путать с обычным activate().
   */
  importSignal: number;

  // ── управление режимом ──────────────────────────────────────────────────
  /** Включить режим (курсор-прицел, ждём тап). */
  activate: () => void;
  /**
   * Включить режим сразу с открытой загрузкой файла (AK-369 «одна дверь»:
   * единственный путь импорта — через режим «+ Поле» с превью на карте).
   */
  activateWithImport: () => void;
  /** Выключить режим (чистый выход, черновик отбрасывается). */
  deactivate: () => void;

  /**
   * Подставить хозяйство снаружи (AK-584): вход «добавить поле в ЭТО
   * хозяйство» — из карточки хозяйства, из дерева структуры, из мастера
   * первого входа (`/map?addField=1&farm=<id>`). Пишем в тот же
   * `sessionFarmId`, что запоминается после сохранения: механизм предвыбора
   * уже был, снаружи его просто никто не заполнял.
   */
  presetFarm: (farmId: string, farmName?: string | null) => void;

  /**
   * Переключить способ добавления (сегмент-переключатель, AK-373). Сбрасывает
   * черновик и возвращает в armed ВЫБРАННОГО способа. Не запускает действие
   * способа сам (для draw нужен явный beginDraw, для detect — тап, для import —
   * дропзона), чтобы переключение было предсказуемым, без скачков.
   */
  setMethod: (method: AddFieldMethod) => void;

  // ── машина ────────────────────────────────────────────────────────────────
  /** Тап по свободному месту → запустить детект (фаза detecting). */
  beginDetect: (point: Point) => void;
  /** Детект вернул контур → фаза draft. */
  setDraft: (geometry: DelineateGeometry, areaHa: number) => void;
  /** Детект не удался/не распознано → тост + назад в armed. */
  failDetect: (message: string) => void;
  /**
   * «Заново»/«Очистить»/«Отмена» — сбросить черновик, вернуться в armed
   * ТЕКУЩЕГО способа (method НЕ меняется — грабля п.5 устранена).
   */
  reset: () => void;
  /** «Редактировать» — передать черновик в редактор (фаза editing). */
  requestEdit: () => void;
  /**
   * Старт способа «Нарисовать» — войти в поточечное рисование (фаза drawing).
   * v2.tsx по manualDrawSignal включает редактор и polygon-draw-mode. «Готово»
   * вернёт контур в форму (как и после «Редактировать»).
   */
  requestManualDraw: () => void;
  /** Редактор вернул уточнённый контур → в форму (фаза form). */
  applyEdited: (geometry: DelineateGeometry, areaHa: number) => void;
  /** Открыть форму сохранения для текущего черновика (фаза form). */
  openForm: () => void;
  /** Сохранение прошло → тост, запомнить хозяйство, назад в armed. */
  completeSave: (message: string, farmId: string, farmName: string) => void;

  // ── тосты ────────────────────────────────────────────────────────────────
  clearErrorToast: () => void;
  clearSuccessToast: () => void;
}

export const useAddFieldStore = create<AddFieldStore>((set) => ({
  active: false,
  method: 'detect',
  phase: 'idle',
  tapPoint: null,
  draftGeometry: null,
  draftAreaHa: null,
  errorToast: null,
  successToast: null,
  sessionFarmId: null,
  sessionFarmName: null,
  savedCount: 0,
  editSignal: 0,
  manualDrawSignal: 0,
  importSignal: 0,

  activate: () =>
    set({
      active: true,
      method: 'detect',
      phase: 'armed',
      tapPoint: null,
      draftGeometry: null,
      draftAreaHa: null,
      errorToast: null,
      // Новый заход в режим = новый счёт сохранений (AK-584).
      savedCount: 0,
    }),
  activateWithImport: () =>
    set((s) => ({
      active: true,
      method: 'import',
      phase: 'armed',
      tapPoint: null,
      draftGeometry: null,
      draftAreaHa: null,
      errorToast: null,
      importSignal: s.importSignal + 1,
      savedCount: 0,
    })),
  deactivate: () =>
    set({
      active: false,
      phase: 'idle',
      tapPoint: null,
      draftGeometry: null,
      draftAreaHa: null,
      errorToast: null,
    }),

  presetFarm: (farmId, farmName = null) =>
    set({ sessionFarmId: farmId, sessionFarmName: farmName }),

  setMethod: (method) =>
    set((s) => ({
      method,
      // Смена способа → чистый armed выбранного способа. Черновик отбрасываем.
      phase: 'armed',
      tapPoint: null,
      draftGeometry: null,
      draftAreaHa: null,
      errorToast: null,
      // «Файл» раскрывает дропзону — толкаем importSignal, чтобы панель открыла
      // загрузку (тот же путь, что и вход из списка полей).
      importSignal: method === 'import' ? s.importSignal + 1 : s.importSignal,
    })),

  beginDetect: (point) => set({ phase: 'detecting', tapPoint: point, errorToast: null }),
  setDraft: (geometry, areaHa) =>
    set({ phase: 'draft', draftGeometry: geometry, draftAreaHa: areaHa }),
  failDetect: (message) => set({ phase: 'armed', tapPoint: null, errorToast: message }),
  reset: () =>
    // «Очистить»/«Отмена»/«Заново» → armed ТЕКУЩЕГО способа (method сохраняем).
    set({ phase: 'armed', tapPoint: null, draftGeometry: null, draftAreaHa: null }),
  requestEdit: () => set((s) => ({ phase: 'editing', editSignal: s.editSignal + 1 })),
  requestManualDraw: () =>
    set((s) => ({
      // Способ «Нарисовать» активен, идёт поточечное рисование.
      method: 'draw',
      phase: 'drawing',
      // Черновик детекта отбрасываем — рисуем с нуля.
      draftGeometry: null,
      draftAreaHa: null,
      tapPoint: null,
      errorToast: null,
      manualDrawSignal: s.manualDrawSignal + 1,
    })),
  applyEdited: (geometry, areaHa) =>
    set({ phase: 'form', draftGeometry: geometry, draftAreaHa: areaHa }),
  openForm: () => set({ phase: 'form' }),
  completeSave: (message, farmId, farmName) =>
    set((s) => ({
      phase: 'armed',
      tapPoint: null,
      draftGeometry: null,
      draftAreaHa: null,
      successToast: message,
      sessionFarmId: farmId,
      sessionFarmName: farmName,
      savedCount: s.savedCount + 1,
    })),

  clearErrorToast: () => set({ errorToast: null }),
  clearSuccessToast: () => set({ successToast: null }),
}));
